// Fetches image metadata from the Commons API

import type { MaybeRefOrGetter } from 'vue'

export function useImageMeta(getUrls: MaybeRefOrGetter<string[]>) {
  const urlsByTitles = computed(() => {
    const map = new Map<string, string>()
    const urls = toValue(getUrls)
    if (urls.length === 0) return map

    for (const imageUrl of urls) {
      const file = imageUrl.split('/').pop()!
      map.set(`Image:${decodeURIComponent(file)}`, imageUrl)
    }
    return map
  })

  const imageMetaUrl = computed(() => {
    if (urlsByTitles.value.size === 0) return ''

    return `https://commons.wikimedia.org/w/api.php?action=query&prop=imageinfo&titles=${[
      ...urlsByTitles.value.keys(),
    ]
      .map(encodeURIComponent)
      .join('|')}&format=json&iiprop=extmetadata&iimetadataversion=latest&origin=*`
  })
  const hasImageMetaUrl = computed(() => imageMetaUrl.value !== '')

  const { isFetching, error, aborted, data } = useFetch(imageMetaUrl, {
    refetch: hasImageMetaUrl,
    immediate: hasImageMetaUrl.value,
  }).json()

  const imageMetaByUrl = computed(() => {
    type Meta = {
      artist: string | undefined
      attribution: boolean
      copyrighted: boolean
      credit: string | undefined
      datetime: string | undefined
    }
    const map = new Map<string, Meta>()
    if (error.value || isFetching.value) return map
    if (!data.value) return map

    const {
      query: { normalized, pages },
    } = data.value
    if (!normalized) return map

    const denormalizedFilenames = new Map<string, string>()
    for (const { from, to } of normalized) {
      denormalizedFilenames.set(to, from)
    }

    for (const pageId in pages) {
      const page = pages[pageId]
      const originalFilename = denormalizedFilenames.get(page.title)!
      if (!urlsByTitles.value.has(originalFilename)) continue

      const url = urlsByTitles.value.get(originalFilename)!
      map.set(url, {
        artist: page.imageinfo[0]?.extmetadata?.Artist?.value ?? undefined,
        attribution: page.imageinfo[0]?.extmetadata?.Attribution?.value === 'False',
        copyrighted: page.imageinfo[0]?.extmetadata?.Copyrighted?.value === 'False',
        credit: page.imageinfo[0]?.extmetadata?.Credit?.value ?? undefined,
        datetime: page.imageinfo[0]?.extmetadata?.DateTimeOriginal?.value ?? undefined,
      })
    }

    return map
  })

  return {
    isFetching,
    error,
    aborted,
    data: imageMetaByUrl,
  }
}
